import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { ClientRefDto, PagedClientRefsDto } from '../../dtos/client-ref.dto';

@Injectable()
export class AppointmentClientLookupRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  // Plain HTTP call against the clients endpoint (same API_URL base the clients feature's own
  // datasource uses) -- NOT an import from `@features/web/clients/...`, to keep feature isolation
  // intact (mirrors patients' PatientsRemoteDatasource.searchTutors).
  //
  // `term` may be empty -- the backend explicitly supports that as "no filter"
  // (ClientsController.SearchClients' own doc comment): a blank term now answers from the cheap
  // paginated client listing instead of ProVet's native "/search/" endpoint (which itself requires
  // a non-blank term), so this dropdown still shows a plain client list before the user types
  // anything. No `sortBy`/`sortDir` here anymore: the backend's client SortSelectors was pruned
  // down to a single "created" key with no page/column tied to it here, so a "firstName" sort
  // request would now silently no-op -- sending it would just be misleading. This dropdown's
  // default order is now whatever ProVet returns natively, not alphabetical.
  public search(term: string): Observable<PagedClientRefsDto> {
    return this.http.get<PagedClientRefsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.search}`, {
      params: { term, page: 1, pageSize: 20 },
    });
  }

  // Same GET /api/clients/{clientId} the clients feature's own datasource uses for its detail
  // page -- returns the full ClientDto, but ClientRefDto only declares the narrow subset of
  // fields (ExternalId/FullName/FirstName/LastName/Email/Patients) this feature actually reads,
  // so the extra fields are simply ignored by the type system.
  public getById(id: string): Observable<ClientRefDto> {
    return this.http.get<ClientRefDto>(`${this.apiUrl}${API_ENDPOINTS.clients.byId(id)}`);
  }

  // Batched variant of getById -- resolves multiple clients in a single request instead of one
  // HTTP call per client id.
  public getByIds(ids: readonly string[]): Observable<PagedClientRefsDto> {
    return this.http.get<PagedClientRefsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.byIds}`, {
      params: { ids: ids.join(',') },
    });
  }
}
